import {
  UiSelect,
  UiInput,
  UiDatepicker,
  UiButton,
  UiAdvancedSelect,
  UiSwitch,
  UiTextarea,
} from "@/components/ui";
import {
  CAMPAGNE_TYPES,
  useCampagne,
  AddCampagneDTOQuerySchema,
} from "@/composables/campagne";
import { ChangeEvent, useEffect, useState } from "react";
import { format, parse, set } from "date-fns";
import { CampagneModel } from "@/app/entities";
import { ZodError } from "zod";
import { useCities } from "@/composables/cities";
import { useValidationErrors } from "@/utils";
import { useToast } from "@/composables/toast";
import { IconButton } from "@radix-ui/themes";
import isBefore from "date-fns/isBefore";

interface CampagneFormProps {
  initialValue?: Partial<CampagneModel>;
}
export function CampagneForm({ initialValue }: CampagneFormProps) {
  const [startDate, setStartDate] = useState<string>("");
  const [draft, setDraft] = useState<boolean>(false);
  const { loading, saveCampagne, updateCampagne } = useCampagne();
  const { regions, loading: loadingVilles, fetchRegions } = useCities();
  const { setError, getFieldErrors } = useValidationErrors();
  const [duree, setDuree] = useState<number>(0);
  const [formValidation, setFormValidation] = useState(false);
  const [typeCampagne, setTypeCampagne] = useState<string>("");
  const [region, setRegion] = useState<string>("");

  useEffect(() => {
    fetchRegions();

    if (initialValue) {
      setRegion(initialValue?.region || "");
      setTypeCampagne(initialValue?.typeCampagne || "");
      try {
        const defaultDate = initialValue?.dateDebut
          ? parse(initialValue?.dateDebut, "yyyy-MM-dd", new Date())
          : null;
        setStartDate(defaultDate ? format(defaultDate, "yyyy-MM-dd") : "");
      } catch (error) {}

      setDraft(initialValue?.draft || false);
      setDuree(initialValue?.dure || 0);
    }
  }, [initialValue]);

  useEffect(() => {
    checkFormValidity();
  }, [initialValue, regions, region, draft]);

  const checkFormValidity = () => {
    const formElement = document.querySelector(
      ".campagn-form"
    ) as HTMLFormElement;
    if (!formElement) return;
    const TheFormData = new FormData(formElement);
    try {
      const estimationEleveur = TheFormData.get("estimationEleveur") as string;
      const objectifCouverture = TheFormData.get(
        "objectifCouverture"
      ) as string;

      const params = {
        typeCampagne: typeCampagne,
        nom: TheFormData.get("nom") as string,
        dateDebut: startDate,
        objectif: TheFormData.get("objectif") as string,
        region: region || "",
        dure: duree || 0,
        estimationEleveur: estimationEleveur
          ? parseInt(`${estimationEleveur}`)
          : 0,
        objectifCouverture: objectifCouverture
          ? parseInt(objectifCouverture)
          : 0,
      };

      AddCampagneDTOQuerySchema.parse(params);
      setFormValidation(true);
    } catch (error) {
      setFormValidation(false);
    }
  };

  const handleSubmit = async () => {
    const formElement = document.querySelector(
      ".campagn-form"
    ) as HTMLFormElement;
    if (!formElement) return;
    const TheFormData = new FormData(formElement);
    try {
      const estimationEleveur = TheFormData.get("estimationEleveur") as string;
      const params = {
        typeCampagne: typeCampagne,
        nom: TheFormData.get("nom") as string,
        dateDebut: startDate,
        objectif: TheFormData.get("objectif") as string,
        region: region,
        dure: duree,
        estimationEleveur: estimationEleveur
          ? parseInt(`${estimationEleveur}`)
          : 0,
        objectifCouverture: parseInt(
          `${TheFormData.get("objectifCouverture") as string}`
        ),
      };

      const validatedData = AddCampagneDTOQuerySchema.parse(params);
      if (initialValue) {
        const initailValue = initialValue as CampagneModel;
        updateCampagne({
          ...initailValue,
          ...validatedData,
          draft,
        });
        return;
      }
      saveCampagne({ ...validatedData, draft });
    } catch (error) {
      if (error instanceof ZodError) {
        setError(error);
        useToast("Erreur de validation", { variant: "error" });
        return;
      }
      useToast("Une erreur s'est produite", { variant: "error" });
    }
  };

  return (
    <div>
      <h2 className="form-title">Créez votre campagne en quelques clics</h2>
      <div className="form-subtitle py-2">
        Organisez facilement un recensement ou une campagne de vaccination pour
        votre cheptel. Planifiez, informez et suivez vos actions en toute
        simplicité.
      </div>
      <form className="campagn-form mt-4">
        <div className="grid gap-4 gap-y-2 grid-cols-1 md:grid-cols-2">
          <div className="w-full ">
            <UiSelect
              className="w-full block pt-1"
              label="Type de la campagne"
              name="type"
              placeholder="Choisissez un type de campagne"
              defaultValue={typeCampagne}
              options={CAMPAGNE_TYPES}
              onChange={(v: string) => {
                setTypeCampagne(v);
                checkFormValidity();
              }}
              styles={{ width: "100%" }}
            ></UiSelect>
          </div>
          <div className="w-full ">
            <UiAdvancedSelect
              className="w-full"
              label="Région"
              name="region"
              loading={loadingVilles}
              defaultValue={region}
              placeholder="Choisir une région"
              options={regions.map((region) => ({
                value: region,
                label: region,
              }))}
              onChange={(v) => {
                setRegion(v || "");
                checkFormValidity();
              }}
            ></UiAdvancedSelect>
          </div>
          <div className="w-full ">
            <UiInput
              label="Nom de la campagne"
              placeholder="Nom de la campagne"
              className="w-full"
              name="nom"
              defaultValue={initialValue?.nom ?? ""}
              onChange={(v: ChangeEvent<HTMLInputElement>) =>
                checkFormValidity()
              }
              errors={getFieldErrors("nom")}
            ></UiInput>
          </div>
          <div className="w-full ">
            <UiInput
              label={
                typeCampagne === "DECLARATION_MAX"
                  ? "Nombre de d’animaux maximum"
                  : "Durée de la campagne (en jours)"
              }
              name="dure"
              type="number"
              placeholder="Durée de la campagne (en jours)"
              className="w-full"
              value={duree}
              onChange={(v: ChangeEvent<HTMLInputElement>) => {
                setDuree(parseInt(v.target.value));
                checkFormValidity();
              }}
              errors={getFieldErrors("dure")}
            ></UiInput>
          </div>
          <div className="w-full col-span-2">
            <UiTextarea
              label="Objectif"
              placeholder="Objectif"
              name="objectif"
              className="w-full"
              defaultValue={initialValue?.objectif ?? ""}
              onChange={(v: ChangeEvent<HTMLTextAreaElement>) =>
                checkFormValidity()
              }
              errors={getFieldErrors("objectif")}
            ></UiTextarea>
          </div>

          <div className="w-full ">
            <UiDatepicker
              name="dateDebut"
              className="w-full"
              label="Date de début"
              shouldDisableDate={(date) =>
                isBefore(
                  date,
                  initialValue?.dateDebut
                    ? parse(initialValue?.dateDebut, "yyyy-MM-dd", new Date())
                    : new Date()
                )
              }
              defaultValue={
                initialValue?.dateDebut
                  ? parse(initialValue?.dateDebut, "yyyy-MM-dd", new Date())
                  : null
              }
              onChange={(v: Date | null) => {
                setStartDate(v ? format(v, "yyyy-MM-dd") : "");
                checkFormValidity();
              }}
              onClean={(e: React.MouseEvent) => {
                setStartDate("");
                checkFormValidity();
              }}
              errors={getFieldErrors("dateDebut")}
            />
          </div>

          <div className="w-full ">
            <UiInput
              label="Estimation d’éleveur"
              placeholder="Estimation d’éleveur"
              className="w-full"
              name="estimationEleveur"
              type="number"
              defaultValue={
                initialValue?.estimationEleveur
                  ? `${initialValue?.estimationEleveur}`
                  : ""
              }
              onChange={(v: ChangeEvent<HTMLInputElement>) =>
                checkFormValidity()
              }
              errors={getFieldErrors("estimationEleveur")}
            ></UiInput>
          </div>

          <div className="w-full ">
            <UiInput
              label="Couverture"
              placeholder="Couverture"
              name="objectifCouverture"
              type="number"
              className="w-full"
              defaultValue={
                initialValue?.objectifCouverture
                  ? `${initialValue?.objectifCouverture}`
                  : ""
              }
              onChange={(v: ChangeEvent<HTMLInputElement>) =>
                checkFormValidity()
              }
              errors={getFieldErrors("objectifCouverture")}
              slotNode={
                <IconButton size="1" variant="ghost">
                  <span className="text-lg">%</span>
                </IconButton>
              }
            ></UiInput>
          </div>
        </div>

        <div className="flex justify-end mt-8">
          <UiSwitch
            className="flex-row items-center  border border-gray-200 border-solid rounded-[8px] px-2"
            label="Brouillon"
            defaultValue={draft}
            onChange={() => {
              setDraft(!draft);
              checkFormValidity();
            }}
          />
        </div>

        <div className="mt-8">
          <UiButton
            disabled={!formValidation}
            onClick={handleSubmit}
            loading={loading}
          >
            {draft ? "Sauvegarder la campagne" : "Lancer la campagne"}
          </UiButton>
        </div>
      </form>
    </div>
  );
}
